import fs from "fs";
import path from "path";
import { RepairRule } from "../repairEngine.js";
import { findPackageJson, safeReadJson, directoryExists } from "../../utils/files.js";

export const packageJsonRepairRules: RepairRule[] = [
  {
    id: "package-json-validation",
    title: "Validate and Format package.json",
    description: "Check if package.json is valid JSON, pretty-print it, and ensure name and version fields exist.",
    safe: true,
    async detect(cwd: string): Promise<boolean> {
      const pPath = findPackageJson(cwd);
      if (!pPath) return false;

      try {
        const raw = fs.readFileSync(pPath, "utf-8");
        // Verify JSON parse
        const parsed = JSON.parse(raw);
        
        // Check missing required fields
        if (!parsed.name || !parsed.version) {
          return true;
        }

        // Check if pretty-printed with 2 spaces
        const formatted = JSON.stringify(parsed, null, 2) + "\n";
        if (raw.trim() !== formatted.trim()) {
          return true;
        }

        return false;
      } catch {
        // Malformed JSON needs repair
        return true;
      }
    },
    async repair(cwd: string): Promise<{ success: boolean; details?: string }> {
      const pPath = findPackageJson(cwd);
      if (!pPath) {
        return { success: false, details: "package.json not found." };
      }

      try {
        let parsed: Record<string, unknown> = {};
        const raw = fs.readFileSync(pPath, "utf-8");

        try {
          parsed = JSON.parse(raw);
        } catch {
          // Attempt to strip comments or simple JSON syntax errors
          const cleaned = raw
            .replace(/\/\*[\s\S]*?\*\/|([^\\:]|^)\/\/.*$/gm, "") // comments
            .replace(/,\s*([}\]])/g, "$1"); // trailing commas
          parsed = JSON.parse(cleaned);
        }

        // Validate and fill required fields
        if (!parsed["name"]) {
          parsed["name"] = path.basename(path.dirname(pPath)) || "react-native-project";
        }
        if (!parsed["version"]) {
          parsed["version"] = "1.0.0";
        }

        // Reformat
        const formatted = JSON.stringify(parsed, null, 2) + "\n";
        fs.writeFileSync(pPath, formatted, "utf-8");

        return { success: true, details: "Formatted and validated package.json." };
      } catch (err: any) {
        return {
          success: false,
          details: `Could not parse package.json: ${err?.message || "Unknown error"}. Please fix JSON syntax errors manually.`,
        };
      }
    },
  },
  {
    id: "missing-node-modules",
    title: "Validate node_modules installation",
    description: "Check if node_modules directory exists. Suggest installation steps if missing.",
    safe: false,
    async detect(cwd: string): Promise<boolean> {
      const pPath = findPackageJson(cwd);
      if (!pPath) return false;
      const root = path.dirname(pPath);
      return !directoryExists(path.join(root, "node_modules"));
    },
    async repair(cwd: string): Promise<{ success: boolean; details?: string }> {
      const pPath = findPackageJson(cwd);
      if (!pPath) return { success: false, details: "package.json not found." };
      const root = path.dirname(pPath);

      // Identify correct package manager based on lockfiles
      let pm = "npm";
      if (fs.existsSync(path.join(root, "yarn.lock"))) pm = "yarn";
      else if (fs.existsSync(path.join(root, "pnpm-lock.yaml"))) pm = "pnpm";
      else if (fs.existsSync(path.join(root, "bun.lock"))) pm = "bun";

      return {
        success: false,
        details: `node_modules is missing. To resolve, run '${pm} install' in the project root.`,
      };
    },
  },
];
